import {FirebaseError} from 'firebase/app';
import {
  Firestore,
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  setLogLevel,
} from 'firebase/firestore';
import {useState} from 'react';
import {useFirebaseApp} from 'reactfire';

/*
 * It is necessary to store the single Firestore instance in a global
 * since it can only be initialized once, and there is no way to determine
 * whether or not it has already been initialized.  Using React.StrictMode
 * causes components to mount multiple times, so without a global it is
 * basically impossible to guarantee that it only gets initialized once,
 * even with the use of hooks.
 */
let firestoreInstance: Firestore | undefined;
let firestoreError: FirebaseError | undefined;

interface UseInitializeFirestore {
  error?: FirebaseError;
  firestore?: Firestore;
}

export function useInitializeFirestore(): UseInitializeFirestore {
  const firebase = useFirebaseApp();

  const [state] = useState<UseInitializeFirestore>(() => {
    if (firestoreInstance) {
      return {firestore: firestoreInstance};
    }
    if (firestoreError) {
      return {error: firestoreError};
    }
    try {
      setLogLevel('info');

      // Enable offline writes by using a persistent cache.
      // Enable tab synchronization by using a persistent multiple tab manager.
      const persistentCache = persistentLocalCache({tabManager: persistentMultipleTabManager()});

      firestoreInstance = initializeFirestore(firebase, {localCache: persistentCache});
      return {firestore: firestoreInstance};
    } catch (error) {
      firestoreError = error as FirebaseError;
      return {error: firestoreError};
    }
  });

  return state;
}
